"use client";
import React, { useEffect } from "react";
import { footerSocialLinks } from "./Helper";
import Link from "next/link";
import Image from "next/image";

const Footer = () => {
  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    const loadScript = () => {
      script = document.createElement("script");
      script.src = "https://www-cdn.icef.com/scripts/iasbadgeid.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);
    };

    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const id = (window as any).requestIdleCallback(loadScript, {
          timeout: 3000,
        });
        return () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ("cancelIdleCallback" in window) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).cancelIdleCallback(id);
          }
          if (script && script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };
      } else {
        const timer = setTimeout(loadScript, 2000);
        return () => {
          clearTimeout(timer);
          if (script && script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };
      }
    }
  }, []);

  return (
    <footer role="contentinfo" className="bg-[linear-gradient(96.2deg,_#8F3EBC_-18.21%,_#0F1217_50.84%,_#1776F0_128.35%)] overflow-hidden z-10 relative">
      <span className="bg-[#5E57EB99] z-0 blur-[224px] size-[400px] rounded-full -bottom-[0%] -left-[0%] absolute"></span>
      <span className="bg-[#3777FFE5] z-0 blur-[224px] size-[400px] rounded-full -top-[0%] -right-[0%] absolute"></span>
      <div className="container relative z-10 2xl:max-w-[1628px] pt-[10px] pb-[35px] sm:py-[35px] lg:py-[45px] xl:py-[54px]">
        <div className="flex lg:flex-row flex-col justify-between pb-5 sm:pb-[30px] xl:pb-[42px] gap-5 sm:gap-8 md:gap-10 2xl:gap-[60px] 3xl:gap-[71px] h-full">
          <div className="flex justify-between items-center gap-8 sm:gap-5 w-full flex-col sm:flex-row">
            <div className="flex flex-col items-center gap-3 sm:gap-5 3xl:gap-[42px] w-full max-w-[200px] md:max-w-[270px] xl:max-w-[382px] mx-auto sm:mx-0">
              <div className="text-white w-full max-w-[335px] xl:max-w-[422px] flex items-center justify-center font-bold">
                <Image
                  className="w-full"
                  src="/logo/svg/whiteLogo.svg"
                  alt="logo"
                  width={382}
                  height={152}
                />
              </div>
              <div className="flex justify-between items-center gap-3 sm:gap-5 2xl:gap-8">
                {footerSocialLinks.map((item) => {
                  return (
                    <Link
                      target="_blank"
                      href={item.url}
                      key={item.id}
                      aria-label={item.name}
                      className="hover:scale-[1.2] duration-300 ease-in-out w-full max-w-[30px] xl:max-w-[40px]"
                    >
                      {item.icon}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* <p className="max-w-[420px] text-center text-xs sm:text-sm text-white/70 ff_poppins">
              Certification badges are displayed only after certificate ID,
              validity date, and scope have been verified.
            </p> */}
          </div>
        </div>

        <div className="border-t border-white/20 flex justify-between pt-5 sm:pt-[30px] xl:pt-[42px] gap-6 sm:gap-[40px] 2xl:gap-[60px] flex-col lg:flex-row">
          <div className="max-w-[840px] grid grid-cols-2 sm:grid-cols-4 w-full gap-6 sm:gap-4">
            {/* Column 1: Company */}
            <div className="flex flex-col">
              <p className="ff_poppins font-semibold text-[13px] sm:text-[15px] md:text-lg xl:text-[20px] text-white mb-4">
                Company
              </p>
              <Link
                href="/about-us"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                About Us
              </Link>
              <Link
                href="/how-it-works"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                How It Works
              </Link>
              <Link
                href="/for-institutions"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                For Institutions
              </Link>
              <Link
                href="/contact-us"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Contact Us
              </Link>
              <Link
                href="/blogs"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Blogs
              </Link>
              <Link
                href="/events"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Events
              </Link>
            </div>

            {/* Column 2: Tools & Services */}
            <div className="flex flex-col">
              <p className="ff_poppins font-semibold text-[13px] sm:text-[15px] md:text-lg xl:text-[20px] text-white mb-4">
                Tools & Services
              </p>
              <Link
                href="/tools/course-finder"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                AI Course Finder
              </Link>
              <Link
                href="/study-abroad-tools"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Study Abroad Tools
              </Link>
              <Link
                href="/book-exam"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Book Exam
              </Link>
              <Link
                href="/visa-processing"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Visa Processing
              </Link>
              <Link
                href="/admission-assistance"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Admission Assistance
              </Link>
              <Link
                href="/sop-interview-preparation"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                SOP Preparation
              </Link>
            </div>

            {/* Column 3: Top Universities */}
            <div className="flex flex-col">
              <p className="ff_poppins font-semibold text-[13px] sm:text-[15px] md:text-lg xl:text-[20px] text-white mb-4">
                Top Universities
              </p>
              <Link
                href="/all-universities/united-kingdom"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                United Kingdom
              </Link>
              <Link
                href="/all-universities/united-states"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                United States
              </Link>
              <Link
                href="/all-universities/australia"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Australia
              </Link>
              <Link
                href="/all-universities/canada"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Canada
              </Link>
              <Link
                href="/all-universities/europe"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Europe
              </Link>
              <Link
                href="/all-universities"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3 underline"
              >
                View All
              </Link>
            </div>

            {/* Column 4: Destinations */}
            <div className="flex flex-col">
              <p className="ff_poppins font-semibold text-[13px] sm:text-[15px] md:text-lg xl:text-[20px] text-white mb-4">
                Destinations
              </p>
              <Link
                href="/country/united-kingdom"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                United Kingdom
              </Link>
              <Link
                href="/country/united-states"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                United States
              </Link>
              <Link
                href="/country/australia"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Australia
              </Link>
              <Link
                href="/country/canada"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Canada
              </Link>
              <Link
                href="/all-countries/europe"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3"
              >
                Europe
              </Link>
              <Link
                href="/all-countries"
                className="ff_poppins font-medium text-xs sm:text-sm md:text-base text-white/90 hover:text-white inline-block mb-3 underline"
              >
                View All
              </Link>
            </div>
          </div>

          <p className="ff_poppins font-medium text-[12px] sm:text-[14px] md:text-lg xl:text-[20px] max-w-[150px] sm:max-w-[235px] hidden xl:flex text-white">
            Corporate Address: <br />
            Plot No - 269, Phase IV, Udyog Vihar, Sector 18, Gurugram, Haryana
            122015
          </p>

          <div className="h-full hidden xl:flex flex-col justify-between gap-5 w-full max-w-[150px] xl:max-w-[200px] bg-white/30 p-5 rounded-2xl">
            <p className="ff_poppins font-medium text-[12px] sm:text-[14px] md:text-lg xl:text-[20px] text-white">
              Call Us
              <br />{" "}
              <Link
                target="_blank"
                href="tel:+91 85519-85519"
                className="underline text-lg"
              >
                +91 85519-85519
              </Link>
            </p>
            <p className="ff_poppins font-medium text-[12px] sm:text-[14px] md:text-lg xl:text-[20px] text-white">
              General Enquiries
              <br />{" "}
              <Link
                target="_blank"
                href="mailto:support@edvia.ai"
                className="underline text-lg"
              >
                support@edvia.ai
              </Link>
            </p>
          </div>
        </div>

        <div className="flex items-start gap-5 sm:gap-7 lg:gap-10 mt-10">
          <p className="ff_poppins font-medium text-[12px] sm:text-[14px] md:text-lg xl:text-[20px] max-w-[150px] sm:max-w-[235px] flex xl:hidden text-white">
            Corporate Address: <br />
            Plot No - 269, Phase IV, Udyog Vihar, Sector 18, Gurugram, Haryana
            122015
          </p>
          <div className="h-full xl:hidden flex flex-col justify-between gap-5 w-full max-w-[140px] sm:max-w-[200px] xl:max-w-[200px] bg-white/30 p-3 sm:p-5 rounded-2xl text-white">
            <p className="ff_poppins font-medium text-[12px] sm:text-[14px] md:text-lg xl:text-[20px]">
              Call Us
              <br />{" "}
              <Link
                target="_blank"
                href="tel:+91 85519-85519"
                className="underline text-xs sm:text-sm md:text-base lg:text-lg"
              >
                +91 85519-85519
              </Link>
            </p>
            <p className="ff_poppins font-medium text-[12px] sm:text-[14px] md:text-lg xl:text-[20px]">
              General Enquiries
              <br />{" "}
              <Link
                target="_blank"
                href="mailto:support@edvia.ai"
                className="underline text-xs sm:text-sm md:text-base lg:text-lg"
              >
                support@edvia.ai
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Legal & Policy Interlinks Row (prevents orphaned pages in sitemap) */}
      <div className="bg-black/30 border-t border-white/15 py-3">
        <div className="container 2xl:max-w-[1628px] flex flex-wrap items-center justify-center gap-x-4 sm:gap-x-6 gap-y-2 text-[11px] sm:text-xs text-white/80 ff_poppins">
          <Link
            href="/policies/privacy-policy"
            className="hover:text-white hover:underline transition-colors"
          >
            Privacy Policy
          </Link>
          <span className="text-white/30 hidden sm:inline">•</span>
          <Link
            href="/policies/terms-conditions"
            className="hover:text-white hover:underline transition-colors"
          >
            Terms & Conditions
          </Link>
          <span className="text-white/30 hidden sm:inline">•</span>
          <Link
            href="/policies/cookies-policy"
            className="hover:text-white hover:underline transition-colors"
          >
            Cookies Policy
          </Link>
          <span className="text-white/30 hidden sm:inline">•</span>
          <Link
            href="/policies/complaints-policy"
            className="hover:text-white hover:underline transition-colors"
          >
            Complaints Policy
          </Link>
          <span className="text-white/30 hidden sm:inline">•</span>
          <Link
            href="/policies/cancellation-refund-policy"
            className="hover:text-white hover:underline transition-colors"
          >
            Cancellation & Refund
          </Link>
          <span className="text-white/30 hidden sm:inline">•</span>
          <Link
            href="/policies/anti-bribery-anti-corruption-policy"
            className="hover:text-white hover:underline transition-colors"
          >
            Anti-Bribery Policy
          </Link>
          <span className="text-white/30 hidden sm:inline">•</span>
          <Link
            href="/gdpr-privacy-policy"
            className="hover:text-white hover:underline transition-colors"
          >
            Compliance Guidelines
          </Link>
        </div>
      </div>

      <div className="bg-white">
        <p className="text-center text-black ff_poppins 2xl:text-lg lg:text-base sm:text-sm text-xs py-3">
          © {new Date().getFullYear()} All Rights Reserved. Daltin Edu Private
          Ltd.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
